/**
 * Resource table container: Pipeline view and Account view with pagination,
 * search filter, expandable sections, and lazy rendering.
 *
 * Extracted from UpgradeMedicPage.tsx — preserves all existing behavior.
 */
import React, { startTransition, useCallback } from 'react';
import {
  Badge,
  Box,
  Checkbox,
  Container,
  ExpandableSection,
  Header,
  Icon,
  Pagination,
  SegmentedControl,
  SpaceBetween,
  StatusIndicator,
  Table
} from '@amzn/awsui-components-core';
import { Account, LambdaFunction, Pipeline } from '../types';
import { GroupedPipeline, FilterType } from '../hooks/useResourceFiltering';
import { DebouncedSearchFilter } from '../components/DebouncedSearchFilter';
import { groupLambdasByStack } from '../utils/jobDisplayUtils';
import styles from '../styles.module.scss';

interface ResourceTableContainerProps {
  // Data
  accounts: Account[];
  groupedPipelines: GroupedPipeline[];
  filteredAccounts: Account[];
  statsData: { totalPipelines: number };
  // View state
  viewMode: 'pipeline' | 'account';
  setViewMode: (mode: 'pipeline' | 'account') => void;
  isViewTransitioning: boolean;
  setIsViewTransitioning: (v: boolean) => void;
  setActiveFilter: (f: FilterType) => void;
  // Search
  handleSearchFilterChange: (text: string) => void;
  // Pagination
  currentPipelinePage: number;
  setCurrentPipelinePage: (page: number) => void;
  currentAccountPage: number;
  setCurrentAccountPage: (page: number) => void;
  PIPELINES_PER_PAGE: number;
  ACCOUNTS_PER_PAGE: number;
  // Expansion
  expandedPipelines: Set<string>;
  togglePipelineExpanded: (id: string) => void;
  // Selection
  selectedLambdaIds: Set<string>;
  selectedLambdaIdsRef: React.MutableRefObject<Set<string>>;
  setSelectedLambdaIds: React.Dispatch<React.SetStateAction<Set<string>>>;
  togglePipelineSelection: (pipeline: Pipeline, e: React.MouseEvent) => void;
  isPipelineSelected: (pipeline: Pipeline) => boolean;
  isPipelinePartiallySelected: (pipeline: Pipeline) => boolean;
  // Column definitions
  lambdaColumnDefinitions: any[];
  lambdaColumnDefinitionsWithSelect: any[];
}

export const ResourceTableContainer: React.FC<ResourceTableContainerProps> = ({
  accounts,
  groupedPipelines,
  filteredAccounts,
  statsData,
  viewMode,
  setViewMode,
  isViewTransitioning,
  setIsViewTransitioning,
  setActiveFilter,
  handleSearchFilterChange,
  currentPipelinePage,
  setCurrentPipelinePage,
  currentAccountPage,
  setCurrentAccountPage,
  PIPELINES_PER_PAGE,
  ACCOUNTS_PER_PAGE,
  expandedPipelines,
  togglePipelineExpanded,
  selectedLambdaIds,
  selectedLambdaIdsRef,
  setSelectedLambdaIds,
  togglePipelineSelection,
  isPipelineSelected,
  isPipelinePartiallySelected,
  lambdaColumnDefinitions,
  lambdaColumnDefinitionsWithSelect
}) => {
  const renderPipeline = useCallback(
    (pipeline: Pipeline) => {
      const isExpanded = expandedPipelines.has(pipeline.id);

      if (pipeline.name === 'Manual Deployed Stacks') {
        const stackGroups = groupLambdasByStack(pipeline.lambdas);
        return (
          <ExpandableSection
            key={pipeline.id}
            variant="default"
            expanded={isExpanded}
            onChange={() => togglePipelineExpanded(pipeline.id)}
            header={
              <SpaceBetween direction="horizontal" size="xs" alignItems="center">
                <div onClick={(e) => togglePipelineSelection(pipeline, e)} style={{ display: 'flex', alignItems: 'center' }}>
                  <Checkbox checked={isPipelineSelected(pipeline)} indeterminate={isPipelinePartiallySelected(pipeline)} onChange={() => {}} />
                </div>
                <Icon name="folder" variant="subtle" />
                <span style={{ fontWeight: 600 }}>Manual Deployed Stacks</span>
                <Badge color="grey">{stackGroups.length} stacks</Badge>
                <Badge color="grey">{pipeline.lambdas.length} lambdas</Badge>
              </SpaceBetween>
            }
          >
            {isExpanded && (
              <SpaceBetween size="s">
                {stackGroups.map(([stackName, lambdas]) => {
                  const stackLambdaIds = lambdas.map((l) => l.id);
                  const allStackSelected = stackLambdaIds.every((id) => selectedLambdaIdsRef.current.has(id));
                  const someStackSelected = stackLambdaIds.some((id) => selectedLambdaIdsRef.current.has(id));

                  const toggleStackSelection = (e: React.MouseEvent) => {
                    e.stopPropagation();
                    startTransition(() => {
                      setSelectedLambdaIds((prev) => {
                        const next = new Set(prev);
                        if (allStackSelected) {
                          stackLambdaIds.forEach((id) => next.delete(id));
                        } else {
                          stackLambdaIds.forEach((id) => next.add(id));
                        }
                        return next;
                      });
                    });
                  };

                  return (
                    <ExpandableSection
                      key={stackName}
                      variant="default"
                      defaultExpanded={false}
                      header={
                        <SpaceBetween direction="horizontal" size="xs" alignItems="center">
                          <div onClick={toggleStackSelection} style={{ display: 'flex', alignItems: 'center' }}>
                            <Checkbox checked={allStackSelected} indeterminate={someStackSelected && !allStackSelected} onChange={() => {}} />
                          </div>
                          <Icon name="status-positive" variant="subtle" />
                          <span style={{ fontWeight: 500 }}>{stackName}</span>
                          <Badge color="blue">{lambdas.length} lambdas</Badge>
                        </SpaceBetween>
                      }
                    >
                      <Table items={lambdas} columnDefinitions={lambdaColumnDefinitions} variant="embedded" stripedRows />
                    </ExpandableSection>
                  );
                })}
              </SpaceBetween>
            )}
          </ExpandableSection>
        );
      }

      if (pipeline.name === 'Manually created') {
        return (
          <ExpandableSection
            key={pipeline.id}
            variant="default"
            expanded={isExpanded}
            onChange={() => togglePipelineExpanded(pipeline.id)}
            header={
              <SpaceBetween direction="horizontal" size="xs" alignItems="center">
                <div onClick={(e) => togglePipelineSelection(pipeline, e)} style={{ display: 'flex', alignItems: 'center' }}>
                  <Checkbox checked={isPipelineSelected(pipeline)} indeterminate={isPipelinePartiallySelected(pipeline)} onChange={() => {}} />
                </div>
                <span style={{ fontWeight: 600 }}>{pipeline.name}</span>
                <Badge color="grey">{pipeline.lambdas.length} lambdas</Badge>
              </SpaceBetween>
            }
          >
            {isExpanded && <Table items={pipeline.lambdas} columnDefinitions={lambdaColumnDefinitionsWithSelect} variant="embedded" stripedRows />}
          </ExpandableSection>
        );
      }

      return (
        <ExpandableSection
          key={pipeline.id}
          variant="default"
          expanded={isExpanded}
          onChange={() => togglePipelineExpanded(pipeline.id)}
          header={
            <SpaceBetween direction="horizontal" size="xs" alignItems="center">
              <div onClick={(e) => togglePipelineSelection(pipeline, e)} style={{ display: 'flex', alignItems: 'center' }}>
                <Checkbox checked={isPipelineSelected(pipeline)} indeterminate={isPipelinePartiallySelected(pipeline)} onChange={() => {}} />
              </div>
              <span style={{ fontWeight: 600 }}>{pipeline.name}</span>
              <Badge color="blue">{pipeline.lambdas.length} lambdas</Badge>
            </SpaceBetween>
          }
        >
          {isExpanded && <Table items={pipeline.lambdas} columnDefinitions={lambdaColumnDefinitions} variant="embedded" stripedRows />}
        </ExpandableSection>
      );
    },
    [
      expandedPipelines,
      togglePipelineExpanded,
      togglePipelineSelection,
      isPipelineSelected,
      isPipelinePartiallySelected,
      lambdaColumnDefinitions,
      lambdaColumnDefinitionsWithSelect,
      selectedLambdaIdsRef,
      setSelectedLambdaIds
    ]
  );

  const renderAccountCard = (account: Account) => (
    <Container
      key={account.id}
      header={
        <Header variant="h3" counter={`${account.pipelines.length} pipelines`}>
          <Icon name="user-profile" /> Account: {account.name}
        </Header>
      }
    >
      <SpaceBetween size="s">{account.pipelines.map((pipeline) => renderPipeline(pipeline))}</SpaceBetween>
    </Container>
  );

  const renderGroupedPipelineCard = (groupedPipeline: GroupedPipeline) => {
    const firstPipeline = groupedPipeline.accounts[0]?.pipeline;
    return (
      <Container
        key={groupedPipeline.pipelineName}
        header={
          <Header variant="h3">
            <SpaceBetween direction="horizontal" size="xs" alignItems="center">
              {firstPipeline && (
                <div onClick={(e) => togglePipelineSelection(firstPipeline, e)} style={{ display: 'flex', alignItems: 'center' }}>
                  <Checkbox
                    checked={isPipelineSelected(firstPipeline)}
                    indeterminate={isPipelinePartiallySelected(firstPipeline)}
                    onChange={() => {}}
                  />
                </div>
              )}
              <Icon name="share" variant="link" />
              <span>Pipeline: {groupedPipeline.pipelineName}</span>
            </SpaceBetween>
          </Header>
        }
      >
        <SpaceBetween size="s">
          {groupedPipeline.accounts.map((accountPipeline) => (
            <ExpandableSection
              key={accountPipeline.pipeline.id}
              variant="default"
              defaultExpanded={false}
              header={
                <SpaceBetween direction="horizontal" size="xs" alignItems="center">
                  <span>
                    <Icon name="user-profile" /> {accountPipeline.accountName}
                  </span>
                  <Badge color="grey">{accountPipeline.pipeline.lambdas.length} lambdas</Badge>
                </SpaceBetween>
              }
            >
              <Table items={accountPipeline.pipeline.lambdas} columnDefinitions={lambdaColumnDefinitions} variant="embedded" stripedRows />
            </ExpandableSection>
          ))}
        </SpaceBetween>
      </Container>
    );
  };

  return (
    <Container
      header={
        <Header
          variant="h2"
          actions={
            <SpaceBetween direction="horizontal" size="m" alignItems="center">
              <DebouncedSearchFilter onFilterChange={handleSearchFilterChange} placeholder="Filter resources" />
              <SegmentedControl
                selectedId={viewMode}
                onChange={({ detail }) => {
                  setIsViewTransitioning(true);
                  setViewMode(detail.selectedId as 'pipeline' | 'account');
                  setActiveFilter(null);
                  setTimeout(() => setIsViewTransitioning(false), 100);
                }}
                options={[
                  { id: 'pipeline', text: `Pipeline (${statsData.totalPipelines})` },
                  { id: 'account', text: `Account (${accounts.length})` }
                ]}
              />
            </SpaceBetween>
          }
        >
          Lambda Resources
        </Header>
      }
    >
      {isViewTransitioning ? (
        <Box textAlign="center" padding="xl">
          <StatusIndicator type="loading">Loading...</StatusIndicator>
        </Box>
      ) : viewMode === 'pipeline' ? (
        groupedPipelines.length > 0 ? (
          <>
            {groupedPipelines
              .slice((currentPipelinePage - 1) * PIPELINES_PER_PAGE, currentPipelinePage * PIPELINES_PER_PAGE)
              .map((gp) => renderGroupedPipelineCard(gp))}
            {groupedPipelines.length > PIPELINES_PER_PAGE && (
              <Box float="right" padding="m">
                <Pagination
                  currentPageIndex={currentPipelinePage}
                  pagesCount={Math.ceil(groupedPipelines.length / PIPELINES_PER_PAGE)}
                  onChange={({ detail }) => setCurrentPipelinePage(detail.currentPageIndex)}
                />
              </Box>
            )}
          </>
        ) : (
          <Box textAlign="center" padding="xxl" color="text-body-secondary">
            No pipelines found
          </Box>
        )
      ) : filteredAccounts.length > 0 ? (
        <>
          {filteredAccounts
            .slice((currentAccountPage - 1) * ACCOUNTS_PER_PAGE, currentAccountPage * ACCOUNTS_PER_PAGE)
            .map((account) => renderAccountCard(account))}
          {filteredAccounts.length > ACCOUNTS_PER_PAGE && (
            <Box float="right" padding="m">
              <Pagination
                currentPageIndex={currentAccountPage}
                pagesCount={Math.ceil(filteredAccounts.length / ACCOUNTS_PER_PAGE)}
                onChange={({ detail }) => setCurrentAccountPage(detail.currentPageIndex)}
              />
            </Box>
          )}
        </>
      ) : (
        <Box textAlign="center" padding="xxl" color="text-body-secondary">
          No resources match the current filter
        </Box>
      )}
    </Container>
  );
};
